import { useState, useRef } from "react";
import { compressImage } from "@/utils/image";
import { fetchAuthed, uploadImage } from "@/utils/api";
import type { SubmitPhotoResponse } from "@/types/api";
import Button from "@/components/ui/Button";
import { Camera, RefreshCw, Send, X } from "lucide-react";

interface CameraCaptureProps {
  gameId: string;
  itemId: string;
  itemName: string;
  onComplete: () => void;
  onCancel: () => void;
}

export default function CameraCapture({
  gameId,
  itemId,
  itemName,
  onComplete,
  onCancel,
}: CameraCaptureProps) {
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    setFile(selected);
    setError(null);
    const url = URL.createObjectURL(selected);
    setPreview(url);
  };

  const handleRetake = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setFile(null);
    setError(null);
    inputRef.current?.click();
  };

  const handleSubmit = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const compressed = await compressImage(file);
      const { uploadUrl } = await fetchAuthed<SubmitPhotoResponse>(
        gameId,
        `/games/${gameId}/submissions`,
        { method: "POST", body: { itemId } },
      );
      await uploadImage(uploadUrl, compressed);
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center">
      <div className="w-full max-w-sm rounded-t-2xl bg-surface p-5 sm:rounded-2xl">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-text">{itemName}</h3>
          <button onClick={onCancel} className="rounded-lg p-1 text-text-secondary hover:bg-surface-sunken hover:text-text">
            <X className="h-5 w-5" />
          </button>
        </div>

        {!preview ? (
          <div className="mt-4">
            <label className="flex cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed border-border py-12 transition-colors hover:border-primary hover:bg-primary/5">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <Camera className="h-8 w-8 text-primary" />
              </div>
              <span className="text-base font-medium text-text-secondary">
                Tap to take a photo
              </span>
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
            <Button
              variant="ghost"
              onClick={onCancel}
              className="mt-3 w-full"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="mt-4">
            <img
              src={preview}
              alt="Preview"
              className="w-full rounded-xl object-cover"
              style={{ maxHeight: "40vh" }}
            />
            {error && (
              <p className="mt-2 text-sm text-error">{error}</p>
            )}
            <div className="mt-3 flex gap-2">
              <Button
                variant="ghost"
                onClick={handleRetake}
                disabled={uploading}
                className="flex-1"
              >
                <RefreshCw className="h-4 w-4" />
                Retake
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                loading={uploading}
                className="flex-1"
              >
                <Send className="h-4 w-4" />
                Submit
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
